import { ReactNode, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { WORKER_POOL_SHARED_VALUE } from "constants/workerPool";
import FormField from "ds/components/Form/Field";
import Select from "ds/components/Select";
import SelectInput from "ds/components/Select/Input";
import { useWorkerPoolOptions } from "shared/WorkerPool/useWorkerPoolOptions";
import { withoutPublicWorkerPool } from "utils/distribution";

const noPublicWorkerPool = withoutPublicWorkerPool();

export const getInitialWorkerPoolValue = (existingId?: string): string => {
  if (existingId) {
    return existingId;
  }

  return noPublicWorkerPool ? "" : WORKER_POOL_SHARED_VALUE;
};

export const getWorkerPoolSubmitValue = (formValue: string): string | undefined => {
  if (!formValue || formValue === WORKER_POOL_SHARED_VALUE) {
    return undefined;
  }

  return formValue;
};

type FormFieldWorkerPoolProps = {
  name: string;
  spaceFieldName: string;
  label?: string;
  tooltipInfo?: ReactNode;
  isOptional?: boolean;
  noMargin?: boolean;
  onChange?: (value: string) => void;
};

const FormFieldWorkerPool = ({
  name,
  spaceFieldName,
  label = "Worker pool",
  tooltipInfo,
  isOptional = false,
  noMargin,
  onChange,
}: FormFieldWorkerPoolProps) => {
  const { control, setValue, watch, trigger } = useFormContext<Record<string, string>>();

  const selectedSpace = watch(spaceFieldName);
  const selectedValue = watch(name);

  const {
    workerPoolsOptions,
    hasData: workerPoolsHasData,
    loading: workerPoolsLoading,
  } = useWorkerPoolOptions(selectedSpace);

  useEffect(() => {
    if (!selectedValue) {
      if (!isOptional && noPublicWorkerPool && workerPoolsOptions.length > 0) {
        setValue(name, workerPoolsOptions[0].value, { shouldValidate: true });
      }
      return;
    }

    if (workerPoolsHasData || (!workerPoolsLoading && !workerPoolsHasData)) {
      const isNotAvailable = !workerPoolsOptions.find(({ value }) => value === selectedValue);
      if (isNotAvailable) {
        setValue(name, "");
      }
    }
  }, [
    name,
    isOptional,
    workerPoolsOptions,
    setValue,
    selectedValue,
    workerPoolsHasData,
    workerPoolsLoading,
  ]);

  // Show validation error if no worker pools are available
  useEffect(() => {
    if (!isOptional && !!selectedSpace && workerPoolsHasData && workerPoolsOptions.length === 0) {
      trigger(name);
    }
  }, [isOptional, selectedSpace, workerPoolsHasData, workerPoolsOptions.length, name, trigger]);

  return (
    <Controller
      name={name}
      control={control}
      rules={
        isOptional
          ? undefined
          : {
              validate: (value) => {
                if (workerPoolsOptions.length === 0) {
                  return "No worker pools available. Please create a worker pool first.";
                }
                if (!value) {
                  return "Worker pool is required";
                }
                return true;
              },
            }
      }
      render={({ field, fieldState }) => (
        <FormField
          noMargin={noMargin}
          label={label}
          error={fieldState.error?.message}
          isOptional={isOptional}
          tooltipInfo={tooltipInfo}
        >
          {({ ariaInputProps }) => (
            <Select
              value={field.value}
              items={workerPoolsOptions}
              onChange={(value) => {
                field.onChange(value);
                onChange?.(value);
              }}
              placeholder={selectedSpace ? "Select worker pool" : "Select a space first"}
              renderInput={(props) => <SelectInput {...props} ref={field.ref} />}
              isError={!!fieldState.error}
              {...ariaInputProps}
            />
          )}
        </FormField>
      )}
    />
  );
};

export default FormFieldWorkerPool;
